import React, { createContext, useContext } from 'react';
import { useCopilotStore } from '../../pages/copilot/stores/useCopilotStore';
import type { ChatSlice } from './chatSlice';

/**
 * Which chat the screen pieces below are showing.
 *
 * A docked chat wraps its panel in a provider with its own store; the pieces
 * read from whatever they are handed, so two chats can be on screen at once
 * without seeing each other. With no provider — the Copilot page — they read
 * the Copilot page's store, exactly as before.
 */
export interface ChatStoreLike {
  <T>(selector: (state: ChatSlice) => T): T;
  getState: () => ChatSlice;
}

const ChatStoreContext = createContext<ChatStoreLike | null>(null);

export function ChatStoreProvider({
  store,
  children,
}: {
  store: ChatStoreLike;
  children: React.ReactNode;
}) {
  return <ChatStoreContext.Provider value={store}>{children}</ChatStoreContext.Provider>;
}

/** The store of the chat being shown. */
export function useChatStoreApi(): ChatStoreLike {
  return useContext(ChatStoreContext) ?? (useCopilotStore as unknown as ChatStoreLike);
}

/** Read from the chat being shown. */
export function useChatStore<T>(selector: (state: ChatSlice) => T): T {
  return useChatStoreApi()(selector);
}
